import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Cards,
  CardsProps,
  Container,
  Header,
  Link,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  TextContent,
} from "@amzn/awsui-components-react";
import {
  InvestigationSample,
  Stage,
} from "../../lusca-search-api/generated-src";
import { useDlqItemLookup } from "../../hooks/useInvestigations";
import {
  RULE_FIELDS,
  RowSeed,
  defaultInvestigationTitle,
  luscaSearchPath,
  sampleToSeed,
  seedValue,
} from "./investigationUtils";
import { TransactionLink } from "./TransactionLink";
import { DlqPkCell } from "./DlqEventModal";
import { OpenInvestigationButton } from "./OpenInvestigationButton";

interface TransactionLookupProps {
  stage: Stage;
  transactionId: string;
  onSelect: (seed: RowSeed) => void;
  onBack: () => void;
}

export { sampleToSeed } from "./investigationUtils";

/** One investigation-to-be: every matching item the default population rule would sweep up. */
export interface RuleGroup {
  key: string;
  /** Seed for the population builder (the group's first item). */
  seed: RowSeed;
  items: InvestigationSample[];
  eventsourcekey?: string;
  errorType?: string;
  errorMessage?: string;
  errorCategory?: string;
  /** Distinct CBE use cases across the group's items. */
  usecases: string[];
  /** Distinct transaction / order IDs across the group (order-ID lookups can span several). */
  transactionIds: string[];
  /** Set when every item is already owned by the same active investigation. */
  coveredBy?: string;
  /** Some, but not all, items are already owned by an investigation. */
  partiallyCovered: boolean;
  /** Distinct investigations owning items of this group (one or more when partially covered). */
  coveringIds: string[];
  /** Items no investigation owns yet; what "Investigate" is about when partially covered. */
  uncovered: InvestigationSample[];
}

/**
 * What the population builder starts from by default (`RULE_FIELDS` with `defaultSelected`): two
 * items whose default rule would be identical seed the same investigation, so choosing between
 * them is meaningless — the user picks a group, not a PK.
 */
export const ruleSignature = (item: InvestigationSample): string => {
  const seed = sampleToSeed(item);
  return RULE_FIELDS.filter((f) => f.defaultSelected)
    .map((f) => seedValue(seed, f.field) ?? "")
    .join("|");
};

const distinct = (values: (string | undefined | null)[]): string[] =>
  Array.from(new Set(values.filter((v): v is string => !!v)));

export function groupByRule(samples: InvestigationSample[]): RuleGroup[] {
  const groups = new Map<string, InvestigationSample[]>();
  samples.forEach((s) => {
    const key = ruleSignature(s);
    groups.set(key, [...(groups.get(key) ?? []), s]);
  });
  return Array.from(groups.entries()).map(([key, items]) => {
    const owners = distinct(items.map((i) => i.primaryInvestigationId));
    const uncovered = items.filter((i) => !i.primaryInvestigationId);
    const everyOwned = uncovered.length === 0;
    return {
      key,
      // Seed from an item nobody owns: the resolver then checks the right PK and the population
      // builder starts from a row the existing investigation's (narrower) rule does not match.
      seed: sampleToSeed(uncovered[0] ?? items[0]),
      items,
      eventsourcekey: items[0].eventsourcekey ?? undefined,
      errorType: items[0].errorType ?? undefined,
      errorMessage: items[0].errorMessage ?? undefined,
      errorCategory: items[0].errorCategory ?? undefined,
      usecases: distinct(items.map((i) => i.usecase)),
      transactionIds: distinct(items.map((i) => i.transactionId ?? i.orderId)),
      coveredBy: everyOwned && owners.length === 1 ? owners[0] : undefined,
      partiallyCovered: !everyOwned && owners.length > 0,
      coveringIds: owners,
      uncovered,
    };
  });
}

const PK_PREVIEW = 3;

const GroupItems: React.FC<{ stage: Stage; group: RuleGroup }> = ({
  stage,
  group,
}) => {
  const [showAll, setShowAll] = useState(false);
  const shown = showAll ? group.items : group.items.slice(0, PK_PREVIEW);
  return (
    <SpaceBetween size="xxs">
      {shown.map((item) => (
        <div key={item.dlqPk}>
          <DlqPkCell
            stage={stage}
            dlqPk={item.dlqPk}
            transactionId={item.transactionId}
            orderId={item.orderId}
          />
        </div>
      ))}
      {group.items.length > PK_PREVIEW && (
        <Button
          variant="inline-link"
          onClick={() => setShowAll((v) => !v)}
          ariaLabel={
            showAll
              ? "Show fewer DLQ items"
              : `Show all ${group.items.length} DLQ items`
          }
        >
          {showAll ? "Show fewer" : `+${group.items.length - PK_PREVIEW} more`}
        </Button>
      )}
    </SpaceBetween>
  );
};

const GroupStatus: React.FC<{ stage: Stage; group: RuleGroup }> = ({
  stage,
  group: g,
}) => {
  if (g.coveredBy)
    return (
      <SpaceBetween direction="horizontal" size="xs" alignItems="center">
        <StatusIndicator type="success">Already investigated</StatusIndicator>
        <OpenInvestigationButton stage={stage} investigationId={g.coveredBy}>
          Open investigation
        </OpenInvestigationButton>
      </SpaceBetween>
    );
  if (g.partiallyCovered) {
    const owned = g.items.length - g.uncovered.length;
    const remaining = g.uncovered.length;
    return (
      <SpaceBetween size="xxs">
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <StatusIndicator type="warning">
            {owned} of {g.items.length} items already in{" "}
            {g.coveringIds.length === 1 ? "an investigation" : "investigations"}
          </StatusIndicator>
          {g.coveringIds.map((id) => (
            <OpenInvestigationButton
              key={id}
              stage={stage}
              investigationId={id}
            >
              Open {id}
            </OpenInvestigationButton>
          ))}
        </SpaceBetween>
        <TextContent>
          <small>
            {g.coveringIds.length === 1 ? "Its rule is" : "Their rules are"}{" "}
            narrower than this group, so the other {remaining}{" "}
            {remaining === 1 ? "item is" : "items are"} not covered.
            Investigating starts from one of those.
          </small>
        </TextContent>
      </SpaceBetween>
    );
  }
  return <StatusIndicator type="pending">Not yet investigated</StatusIndicator>;
};

/* eslint-disable react/no-unstable-nested-components -- Cards renderers are plain functions, created once per stage */
const cardDefinition = (
  stage: Stage
): CardsProps.CardDefinition<RuleGroup> => ({
  header: (g) => (
    <SpaceBetween direction="horizontal" size="xs" alignItems="center">
      <Box variant="h4" display="inline">
        {g.errorType || "Unknown error"}
      </Box>
      <Badge color="blue">
        {g.items.length} {g.items.length === 1 ? "item" : "items"}
      </Badge>
    </SpaceBetween>
  ),
  sections: [
    {
      id: "queue",
      header: "Queue",
      content: (g) => g.eventsourcekey || "—",
    },
    {
      id: "message",
      header: "Templated error message",
      content: (g) => (
        <span style={{ wordBreak: "break-word" }}>{g.errorMessage || "—"}</span>
      ),
    },
    {
      id: "usecase",
      header: "Use case",
      content: (g) =>
        g.usecases.length ? (
          <SpaceBetween direction="horizontal" size="xxs">
            {g.usecases.map((u) => (
              <Badge key={u}>{u}</Badge>
            ))}
          </SpaceBetween>
        ) : (
          "—"
        ),
    },
    {
      id: "txn",
      header: "Transactions",
      content: (g) => (
        <SpaceBetween direction="horizontal" size="xs">
          {g.transactionIds.map((id) => (
            <TransactionLink key={id} stage={stage} transactionId={id} />
          ))}
        </SpaceBetween>
      ),
    },
    {
      id: "items",
      header: "DLQ items",
      content: (g) => <GroupItems stage={stage} group={g} />,
    },
    {
      id: "status",
      header: "Status",
      content: (g) => <GroupStatus stage={stage} group={g} />,
    },
  ],
});
/* eslint-enable react/no-unstable-nested-components */

/**
 * DLQ items matching a transaction/order ID, grouped into the investigations they would seed.
 *
 * Each card is one distinct default population rule (queue + error type + templated message +
 * category) with every matching item under it; the user investigates a group, not a PK. Groups an
 * active investigation already covers are not selectable and link to that case instead. After
 * creating an investigation the drawer returns here, so the next group is one click away.
 */
export const TransactionLookup: React.FC<TransactionLookupProps> = ({
  stage,
  transactionId,
  onSelect,
  onBack,
}) => {
  const lookup = useDlqItemLookup(stage, { transactionId });
  const [selectedKey, setSelectedKey] = useState<string | null>(null);

  const groups = groupByRule(lookup.samples);
  const open = groups.filter((g) => !g.coveredBy);
  const selected = groups.find((g) => g.key === selectedKey) ?? null;

  // Preselect the first group nobody is investigating yet, so the CTA is one click away.
  useEffect(() => {
    if (lookup.pending || groups.length === 0) return;
    setSelectedKey((current) =>
      current && open.some((g) => g.key === current)
        ? current
        : open[0]?.key ?? null
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- derived from samples
  }, [lookup.pending, lookup.samples]);

  const hasResults =
    !lookup.pending && !lookup.notFound && lookup.samples.length > 0;
  const total = lookup.count ?? lookup.samples.length;

  const summary = (() => {
    if (groups.length === 1)
      return `${
        total === 1 ? "One DLQ item matches" : `${total} DLQ items match`
      } this transaction and share one queue and error — a single investigation covers ${
        total === 1 ? "it" : "all of them"
      }.`;
    const covered = groups.length - open.length;
    const partial = groups.filter((g) => g.partiallyCovered).length;
    const notes = [
      covered ? `${covered} already ${covered === 1 ? "has" : "have"} one` : "",
      partial
        ? `${partial} ${partial === 1 ? "is" : "are"} partly covered by one`
        : "",
    ].filter(Boolean);
    return `${total} DLQ items match this transaction and fall into ${
      groups.length
    } distinct errors, each its own investigation${
      notes.length ? ` — ${notes.join(", ")}` : ""
    }. Pick a group to investigate; you return here afterwards for the next.`;
  })();

  return (
    <SpaceBetween size="m">
      <Container
        header={
          <Header
            variant="h3"
            description={`DLQ items in the latest hourly ${stage} snapshot matching this transaction or order ID`}
            actions={
              <Link href={luscaSearchPath(stage, transactionId)} external>
                Open in Lusca Search
              </Link>
            }
          >
            Transaction {transactionId}
          </Header>
        }
        footer={
          hasResults && (
            <Box float="right">
              <SpaceBetween direction="horizontal" size="xs">
                <Button variant="link" onClick={onBack}>
                  Back
                </Button>
                <Button
                  variant="primary"
                  iconName="search"
                  disabled={!selected}
                  onClick={() => selected && onSelect(selected.seed)}
                >
                  {(() => {
                    if (!selected) return "Investigate";
                    const n = selected.partiallyCovered
                      ? selected.uncovered.length
                      : selected.items.length;
                    const noun = n === 1 ? "item" : "items";
                    if (selected.partiallyCovered)
                      return `Investigate remaining ${n} ${noun}`;
                    return groups.length > 1
                      ? `Investigate selected group (${n} ${noun})`
                      : "Investigate";
                  })()}
                </Button>
              </SpaceBetween>
            </Box>
          )
        }
      >
        {lookup.pending && (
          <Box textAlign="center" padding="s">
            <Spinner /> Searching the DLQ snapshot…
          </Box>
        )}
        {lookup.error && (
          <Alert type="error" header="Lookup failed">
            {lookup.error.message}
          </Alert>
        )}
        {lookup.notFound && (
          <Alert type="info">
            No DLQ items match this transaction in the latest snapshot. If the
            failure is recent it may appear after the next hourly snapshot;
            check the transaction itself in{" "}
            <Link href={luscaSearchPath(stage, transactionId)} external>
              Lusca Search
            </Link>
            .
          </Alert>
        )}
        {hasResults && (
          <SpaceBetween size="s">
            <TextContent>
              <small>
                {summary}
                {total > lookup.samples.length &&
                  ` Showing the first ${lookup.samples.length} items.`}
              </small>
            </TextContent>
            <Cards
              items={groups}
              trackBy="key"
              cardsPerRow={[{ cards: 1 }, { minWidth: 1100, cards: 2 }]}
              selectionType={groups.length > 1 ? "single" : undefined}
              // Click anywhere on the card, not just the radio. Clicks on the links inside
              // (PKs, transactions) also land here, which is harmless: it selects the group
              // the user is already looking at.
              entireCardClickable
              selectedItems={selected ? [selected] : []}
              onSelectionChange={({ detail }) => {
                const next = detail.selectedItems[0];
                // A whole-card click still fires for disabled (covered) cards; ignore it.
                if (next && !next.coveredBy) setSelectedKey(next.key);
              }}
              isItemDisabled={(g) => !!g.coveredBy}
              ariaLabels={{
                itemSelectionLabel: (_, g) =>
                  `Investigate ${defaultInvestigationTitle(g.seed)}`,
                selectionGroupLabel: "DLQ error groups",
              }}
              cardDefinition={cardDefinition(stage)}
            />
          </SpaceBetween>
        )}
      </Container>
      {!hasResults && (
        <Button variant="link" onClick={onBack}>
          Back
        </Button>
      )}
    </SpaceBetween>
  );
};

export default TransactionLookup;
